import { useState, useEffect, useCallback, useRef } from 'react';
import { getIngredientsAutocomplete } from '@/lib/apiService';
import type { ClinicalMealPlanWarning } from '@/lib/apiService';
import { useDebounce } from './useDebounce';

interface UseDebouncedIngredientSearchOptions {
  /** Wait this many ms after user stops typing before calling API (300–500ms recommended). */
  delay?: number;
  /** Minimum query length before making API request (reduces calls for single characters). */
  minLength?: number;
  searchType?: 'basic' | 'with_units' | 'units_only';
  source?: 'all' | 'spoonacular' | 'edamam' | 'internal';
  allergies?: string[] | string;
  clientId?: string;
  /** When false, skips all API calls (e.g. when search UI is not shown). */
  enabled?: boolean;
}

interface IngredientSearchItem {
  id: string;
  name: string;
  description?: string | null;
  possibleUnits?: string[];
  allergens?: string[];
  allergenTagIds?: string[];
  dietaryTagIds?: string[];
  dietLabels?: string[];
  intoleranceTagIds?: string[];
  intoleranceTags?: string[];
  source?: string;
  sourceId?: string;
  provider?: string;
  isCached?: boolean;
  tagClassificationStatus?: 'not_cached' | 'queued' | 'processing' | 'completed' | 'failed';
  tagClassificationSource?: 'preview' | 'ai' | 'none';
  tagClassificationUpdatedAt?: string | null;
  internalId?: string;
  spoonacularId?: string;
  yieldFactor?: number | null;
  retentionFactor?: number | null;
  image?: string;
  imageUrl?: string;
  aisle?: string;
  clinicalWarnings?: ClinicalMealPlanWarning[];
}

export const useDebouncedIngredientSearch = (
  query: string,
  options: UseDebouncedIngredientSearchOptions = {}
) => {
  const { delay = 400, minLength = 2, searchType = 'basic', source, allergies, clientId, enabled = true } = options;
  const [ingredients, setIngredients] = useState<string[]>([]);
  const [ingredientItems, setIngredientItems] = useState<IngredientSearchItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestSequenceRef = useRef(0);
  const lastSuccessfulRequestKeyRef = useRef<string | null>(null);
  const inFlightRequestKeyRef = useRef<string | null>(null);

  const debouncedQuery = useDebounce(query, delay);

  const searchIngredients = useCallback(async (searchQuery: string) => {
    const requestId = ++requestSequenceRef.current;
    const trimmed = searchQuery.trim();
    const allergiesKey = Array.isArray(allergies)
      ? allergies.map((entry) => String(entry ?? '').trim()).filter(Boolean).join(',')
      : String(allergies ?? '').trim();
    const requestKey = `${searchType}|${source ?? 'default'}|${clientId ?? ''}|${allergiesKey}|${trimmed.toLowerCase()}`;
    if (!trimmed) {
      setIngredients([]);
      setIngredientItems([]);
      setLoading(false);
      lastSuccessfulRequestKeyRef.current = null;
      inFlightRequestKeyRef.current = null;
      return;
    }
    if (trimmed.length < minLength) {
      setIngredients([]);
      setIngredientItems([]);
      setLoading(false);
      lastSuccessfulRequestKeyRef.current = null;
      inFlightRequestKeyRef.current = null;
      return;
    }
    if (
      requestKey === lastSuccessfulRequestKeyRef.current ||
      requestKey === inFlightRequestKeyRef.current
    ) {
      return;
    }

    inFlightRequestKeyRef.current = requestKey;
    setLoading(true);
    setError(null);

    try {
      const response = await getIngredientsAutocomplete(
        trimmed,
        searchType,
        undefined,
        source,
        allergies,
        clientId
      );
      if (requestId !== requestSequenceRef.current) {
        return;
      }
      lastSuccessfulRequestKeyRef.current = requestKey;
      setIngredients(response.suggestions || []);
      const items: IngredientSearchItem[] = response.items
        ? response.items.map((item, index) => {
            const ingredientName = String(item.name ?? '').trim();
            const resolvedImageUrl = typeof item.imageUrl === 'string' && item.imageUrl.trim().length > 0
              ? item.imageUrl
              : typeof item.image === 'string' && item.image.trim().length > 0
                ? item.image
                : undefined;
            return {
              id: String(item.id ?? `ingredient-${index}-${ingredientName}`),
              name: ingredientName,
              description: typeof item.description === 'string' && item.description.trim().length > 0
                ? item.description.trim()
                : null,
              possibleUnits: item.possibleUnits ?? [],
              allergens: Array.isArray(item.allergens) ? item.allergens : [],
              allergenTagIds: Array.isArray(item.allergenTagIds) ? item.allergenTagIds : [],
              dietaryTagIds: Array.isArray(item.dietaryTagIds) ? item.dietaryTagIds : [],
              dietLabels: Array.isArray(item.dietLabels) ? item.dietLabels : [],
              intoleranceTagIds: Array.isArray(item.intoleranceTagIds) ? item.intoleranceTagIds : [],
              intoleranceTags: Array.isArray(item.intoleranceTags) ? item.intoleranceTags : [],
              source: item.source,
              sourceId: item.sourceId,
              provider: item.provider,
              isCached: item.isCached,
              tagClassificationStatus: item.tagClassificationStatus,
              tagClassificationSource: item.tagClassificationSource,
              tagClassificationUpdatedAt: item.tagClassificationUpdatedAt,
              internalId: item.internalId,
              spoonacularId: item.spoonacularId,
              yieldFactor: item.yieldFactor ?? null,
              retentionFactor: item.retentionFactor ?? null,
              image: resolvedImageUrl,
              imageUrl: resolvedImageUrl,
              aisle: item.aisle,
              clinicalWarnings: Array.isArray(item.clinicalWarnings) ? item.clinicalWarnings : [],
            };
          }).filter((item) => item.name.length > 0)
        : (response.suggestions ?? []).map((name, index) => ({
            id: `ingredient-${index}-${name}`,
            name,
            possibleUnits: [] as string[],
            allergens: [] as string[],
            source: undefined,
          }));
      setIngredientItems(items);
    } catch (err) {
      if (requestId !== requestSequenceRef.current) {
        return;
      }
      lastSuccessfulRequestKeyRef.current = null;
      console.error('Error fetching ingredients:', err);
      setError('Failed to fetch ingredients. Please try again.');
      setIngredients([]);
      setIngredientItems([]);
    } finally {
      if (requestId === requestSequenceRef.current) {
        inFlightRequestKeyRef.current = null;
      }
      if (requestId === requestSequenceRef.current) {
        setLoading(false);
      }
    }
  }, [searchType, source, allergies, clientId, minLength]);

  useEffect(() => {
    if (!enabled) {
      requestSequenceRef.current += 1;
      inFlightRequestKeyRef.current = null;
      setIngredients([]);
      setIngredientItems([]);
      setLoading(false);
      return;
    }
    searchIngredients(debouncedQuery);
  }, [enabled, debouncedQuery, searchIngredients]);

  // Format for SearchableDropdown: use full items when available (id, name, possibleUnits)
  const formattedResults = ingredientItems.length > 0
    ? ingredientItems.map((item) => ({
        id: item.id ?? `ingredient-${item.name}`,
        name: item.name,
        description: item.description ?? null,
        possibleUnits: item.possibleUnits ?? [],
        allergens: item.allergens ?? [],
        allergenTagIds: item.allergenTagIds ?? [],
        dietaryTagIds: item.dietaryTagIds ?? [],
        dietLabels: item.dietLabels ?? [],
        intoleranceTagIds: item.intoleranceTagIds ?? [],
        intoleranceTags: item.intoleranceTags ?? [],
        source: item.source,
        sourceId: item.sourceId,
        provider: item.provider,
        isCached: item.isCached,
        tagClassificationStatus: item.tagClassificationStatus,
        tagClassificationSource: item.tagClassificationSource,
        tagClassificationUpdatedAt: item.tagClassificationUpdatedAt,
        internalId: item.internalId,
        spoonacularId: item.spoonacularId,
        yieldFactor: item.yieldFactor ?? null,
        retentionFactor: item.retentionFactor ?? null,
        image: item.image ?? item.imageUrl,
        imageUrl: item.imageUrl ?? item.image,
        aisle: item.aisle,
        clinicalWarnings: item.clinicalWarnings ?? [],
      }))
    : ingredients.map((name, index) => ({
        id: `ingredient-${index}-${name}`,
        name,
        description: null,
        possibleUnits: [] as string[],
        allergens: [] as string[],
        source: undefined,
        sourceId: undefined,
        provider: undefined,
        isCached: undefined,
        internalId: undefined,
        spoonacularId: undefined,
        yieldFactor: undefined,
        retentionFactor: undefined,
      }));

  return { 
    ingredients, 
    formattedResults,
    ingredientItems,
    loading, 
    error 
  };
};
